import React, { memo } from "react";
import {
  ProductCardProductsType,
  ProductCardCompareLabelWeightsType,
  ProductCardIsinToNameType,
} from "types/AssistantTypes";
import styles from "./informazioniGeneraliCompare.module.scss";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "shared/components/ui/tooltip";
import { QuestionMarkIcon } from "shared/assets/svg/QuestionMarkIcon";

type InformazioniGeneraliProps = {
  data: ProductCardCompareLabelWeightsType[];
  allProducts: ProductCardProductsType;
  isinToName: ProductCardIsinToNameType;
  productTypes: Record<string, string>;
};

const InformazioniGeneraliComponent: React.FC<InformazioniGeneraliProps> = ({
  data,
  allProducts,
  isinToName,
  productTypes,
}) => {
  const { t } = useTranslation();

  const productIsins = allProducts;
  const columnCount = productIsins.length + 1; // +1 for labels column

  // Render header row (product names)
  const headerRow = (
    <>
      <div className={styles.cell}></div>
      {productIsins.map((isin) => (
        <div key={isin} className={styles.cell}>
          {isinToName[isin]}
        </div>
      ))}
    </>
  );

  // Render data rows
  const dataRows = data.map((row) => {
    const isCostiTotali = row.label === "Costi totali stimati*";

    return (
      <React.Fragment key={row.label}>
        <div className={styles.cellKeys}>
          {isCostiTotali ? (
            <span className={styles.labelWithTooltip}>
              {row.label.replace("*", "")}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={styles.infoIcon}>
                      <QuestionMarkIcon viewBox="0.5 0.5 17 17" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className={styles.tooltipContent}>
                    {t("productCard.costiTotalStimatiDescription")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </span>
          ) : (
            row.label
          )}
        </div>
        {row.weights.map((value, colIndex) => (
          <div key={`${row.label}-${colIndex}`} className={styles.cellValue}>
            {value || "-"}
          </div>
        ))}
      </React.Fragment>
    );
  });

  return (
    <>
      <h3 className={styles.title}>{t("productCard.mainInfo")}</h3>
      <div className={styles.container}>
        <div className={`${styles.grid} ${styles[`grid${columnCount}`]}`}>
          {headerRow}
          {dataRows}
        </div>
      </div>
    </>
  );
};

// Use React.memo to prevent re-renders if props haven't changed
export const InformazioniGenerali = memo(
  InformazioniGeneraliComponent,
  (prevProps, nextProps) => {
    // Only re-render if data changes
    return JSON.stringify(prevProps.data) === JSON.stringify(nextProps.data);
  },
);

export default InformazioniGenerali;
